import type { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { nameof } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';

import type {
  ReferencesContext_Resolver,
  ReferencesContext_ResolverGroup,
  ReferencesContext_ResolverReturnType,
} from '../../contexts/ReferencesContext';

import { CommandInsertionAction } from './CommandInsertionAction';
import { DirectGenerationAction } from './DirectGenerationAction';
import type { OnSelectHandler } from './types/on-select-handler.type';

export const RESOLVER_GROUP = 'group';
export const RESOLVER_RETURN_TYPE = 'returnType';

interface Params {
  readonly resolverGroups: readonly ReferencesContext_ResolverGroup[];
  readonly resolverReturnTypes: readonly ReferencesContext_ResolverReturnType[];
  readonly enableActions: (
    params: Readonly<GridRowParams<ReferencesContext_Resolver>>,
  ) => boolean;
  readonly enableDirectGeneration: boolean;
  readonly handleSelect: OnSelectHandler;
  readonly setIsLoading: (isLoading: boolean) => void;
}

export const getColumns = ({
  resolverGroups,
  resolverReturnTypes,
  enableActions,
  enableDirectGeneration,
  handleSelect,
  setIsLoading,
}: Readonly<Params>): readonly GridColDef<ReferencesContext_Resolver>[] => [
  {
    field: 'actions',
    headerName: 'Actions',
    description: 'Actions',
    editable: false,
    sortable: false,
    type: 'actions',
    getActions: (
      params: Readonly<GridRowParams<ReferencesContext_Resolver>>,
    ) =>
      enableActions(params)
        ? [
            <CommandInsertionAction
              key={`commandInsertionAction-${params.row._id}`}
              onSelect={handleSelect}
              resolver={params.row}
            />,
            ...(params.row.enableDirectGeneration && enableDirectGeneration
              ? [
                  <DirectGenerationAction
                    key={`directGenerationAction-${params.row._id}`}
                    onSelect={handleSelect}
                    resolver={params.row}
                    setIsLoading={setIsLoading}
                  />,
                ]
              : EMPTY_ARRAY),
          ]
        : EMPTY_ARRAY,
  },
  {
    field: nameof<ReferencesContext_Resolver>('code'),
    headerName: 'Code',
    width: 200,
    description: 'Code',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<ReferencesContext_Resolver>('label'),
    headerName: 'Libellé',
    width: 400,
    description: 'Libellé',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<ReferencesContext_Resolver>('description'),
    headerName: 'Description',
    width: 400,
    description: 'Description',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: nameof<ReferencesContext_Resolver>('command'),
    headerName: 'Commande',
    width: 200,
    description: 'Commande',
    editable: false,
    sortable: true,
    type: 'string',
  },
  {
    field: RESOLVER_RETURN_TYPE,
    headerName: 'Type de retour',
    width: 200,
    description: 'Type de retour',
    editable: false,
    sortable: true,
    type: 'singleSelect',
    valueGetter: (_: unknown, row: ReferencesContext_Resolver) =>
      resolverReturnTypes.find(
        (returnType) => returnType._id === row.returnTypeId,
      )?.code ?? 'Inconnu',
    valueOptions: resolverReturnTypes.map((returnType) => ({
      value: returnType.code,
      label: returnType.label,
    })),
  },
  {
    field: RESOLVER_GROUP,
    headerName: 'Groupe',
    width: 200,
    description: 'Groupe',
    editable: false,
    sortable: true,
    type: 'singleSelect',
    valueGetter: (_: unknown, row: ReferencesContext_Resolver) =>
      resolverGroups.find((group) => group._id === row.groupId)?.code ??
      'Inconnu',
    valueOptions: resolverGroups.map((group) => ({
      value: group.code,
      label: group.label,
    })),
  },
  {
    field: nameof<ReferencesContext_Resolver>('isResolvableOnFront'),
    headerName: 'Résolvable sur le client ?',
    width: 100,
    description: 'Résolvable sur le client ?',
    editable: false,
    sortable: true,
    type: 'boolean',
  },
  {
    field: nameof<ReferencesContext_Resolver>('isResolvableOnBack'),
    headerName: 'Résolvable sur le serveur ?',
    width: 100,
    description: 'Résolvable sur le serveur ?',
    editable: false,
    sortable: true,
    type: 'boolean',
  },
];
